<template>
  <div v-if="basket" class="warehouse-basket">
    <div class="basket-header">
      <div class="basket-header__title">
        <h2 class="primary--text">Warenkorb</h2>
        <div class="regenerate mt-1 cursor-pointer op-hover">
          <div class="d-flex align-center mt-2 ml-2" @click="regenerateBasket">
            <div class="icon-neu-laden" style="font-size: 25px">
            </div>
            <div class="ml-3">
              Warenkorb aus Aufträgen neu generieren
            </div>
            <v-tooltip
                bottom
                color="#F4EDE7"
            >
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="icon-informationen cursor-help generate-tooltip-icon"></span>
              </template>

              <div class="pa-4 generate-tooltip">
                Der <b>Bedarf aller Stockwerke</b> seit Ihrer letzten Bestellung wird automatisch in den Warenkorb
                geladen.
              </div>
            </v-tooltip>
          </div>
          <small v-if="basket && basket.isGenerated" class="generated-text text--darkestgrey">
            Warenkorb generiert am {{ $moment(basket.created_at).format('DD.MM.YYYY HH:mm') }}
          </small>
        </div>

        <div class="basket-header__title__actions">

          <v-btn
              v-if="basket.isGenerated || basket.status === 'confirmed'"
              @click="emptyOrder"
              text
              class="primary-light-button"
          >
            Neuen leeren Warenkorb erstellen

            <v-tooltip
                bottom
                color="#F4EDE7"
            >
              <template v-slot:activator="{ on, attrs }">
                <span v-bind= "attrs" v-on="on" class="icon-informationen cursor-help generate-tooltip-icon"></span>
              </template>

              <div class="pa-4 generate-tooltip">
                <b>Entfernt</b> alle Artikel aus<br> Ihrem Warenkorb.
              </div>
            </v-tooltip>
          </v-btn>
        </div>
      </div>


      <div class="basket-header__actions" v-if="!isLoading && basket && basket.address && basket.status !== 'confirmed'">
        <div class="shipping-address" v-if="!isEmptyBasket">
          <b>Lieferadresse</b>
          <span>{{ basket.address.company }}</span>
          <span v-if="basket.address.adresszeile1">{{ basket.address.adresszeile1 }}</span>
          <span v-if="basket.address.adresszeile2">{{ basket.address.adresszeile2 }}</span>
          <span>{{ basket.address.street }} {{ basket.address.number }}</span>
          <span>{{ basket.address.zip }} {{ basket.address.city }}</span>
        </div>
        <div v-else>
          Ihr Warenkorb ist leer.
        </div>

        <div class="basket-actions">
          <ArticlePickerDialog
              type="basket"
              currentView="basket"
              module="basket"
              title="Artikel für *Hauptlager* hinzufügen"
              label="Artikel hinzufügen"
          />
        </div>
      </div>
    </div>
    <template v-if="basket.status === 'confirmed'">
      <div class="mt-8 ">
       <b>Ihre Bestellung wurde erfolgreich übermittelt.</b>
      </div>
    </template>
    <template v-else-if="!isLoading && basket">

      <template v-if="!isEmptyBasket">
        <OrderBasketHeader class="-bordered"/>
        <template v-for="article in articles">
          <BasketArticle :article="article"/>
        </template>

        <template v-if="collectiveArticles.length > 0">
          <div class="basket-article -plan-c -headline -inline">
            <div class="basket-article__headline">
              <span class="icon-gemeinschaftsartikel mr-4"></span>
              <span class="mt-1 text--darkestgrey">
              {{ $t('sl.warehouse.sections.collective-articles.title') }}
            </span>
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on" class="icon-informationen text--darkestgrey ml-2"></span>
                </template>
                <div style="max-width: 380px;" v-html="$t('sl.warehouse.sections.collective-articles.text')">
                </div>
              </v-tooltip>
            </div>
            <div class="basket-article__input"></div>
            <div class="basket-article__unit"></div>
            <div class="basket-article__total"></div>
          </div>

          <OrderBasketHeader class="-inline">
            <template #quantity>
              <HeaderLabel :text="$t('sl.basket.headers.model_'+planModel+'.quantitySpecific')"
                           addClasses="justify-end text-right"/>
            </template>

            <template #input>
              <HeaderLabel :text="$t('sl.basket.headers.model_'+planModel+'.adjustOrderSpecific')"/>
            </template>
          </OrderBasketHeader>

          <template v-for="article in collectiveArticles">
            <BasketArticle :article="article"/>
          </template>
        </template>

        <BasketTotals/>

        <BasketAdditionalInfos />

        <div class="basket__actions d-flex justify-space-between align-center">
          <div class="basket__actions__terms">
            <BasketAGB v-model="termsAccepted"/>
          </div>

          <div class="basket__actions__submit">
            <v-btn
                color="primary"
                depressed
                medium
                class="order-button"
                @click="submitOrder"
                :disabled="!termsAccepted"
            >
              <div class="d-flex w-full mt-1">
                <div>
                  <span class="icon-warenkrob"></span>
                </div>
                <div class="ml-4">
                  Jetzt bestellen
                </div>
              </div>
            </v-btn>
          </div>
        </div>
      </template>
    </template>
    <div v-else>
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
    </div>
  </div>
</template>

<script>
import httpClient from "@/utils/httpClient";
import OrderBasketHeader from "@/components/SmartLogistics/Order/Basket/OrderBasketHeader.vue";
import BasketTotals from "@/components/SmartLogistics/Order/Basket/BasketTotals.vue";
import BasketArticle from "@/components/SmartLogistics/Order/Basket/BasketArticle.vue";
import is from "vue2-datepicker/locale/es/is";
import BasketAdditionalInfos from "@/components/SmartLogistics/Order/Basket/BasketAdditionalInfos.vue";
import BasketAGB from "@/components/SmartLogistics/Order/Basket/BasketAGB.vue";
import BasketOrderButton from "@/components/SmartLogistics/Order/Basket/BasketOrderButton.vue";
import ArticlePickerDialog from "@/components/SmartLogistics/Article/ArticlePickerDialog.vue";
import {mapGetters} from "vuex";
import OrderArticleSpacer from "@/components/SmartLogistics/Warehouse/Partials/OrderArticleSpacer.vue";
import HeaderLabel from "@/components/SmartLogistics/Design/HeaderLabel.vue";
import basket from "../../Order/Basket/Basket.vue";

export default {
  name: 'Order',
  components: {
    HeaderLabel,
    OrderArticleSpacer,
    ArticlePickerDialog,
    BasketOrderButton, BasketAGB, BasketAdditionalInfos, BasketArticle, BasketTotals, OrderBasketHeader
  },

  data() {
    return {
      currentOrder: null,
      termsAccepted: false,
    }
  },

  computed: {
    ...mapGetters({
      basket: 'smartLogisticBasket/basket',
      basketItems: 'smartLogisticBasket/basketItems',
      planModel: 'smartLogisticSettings/planModel',
    }),

    isLoading() {
      return this.$store.state.smartLogisticWarehouse.loadingQueue.length > 0
    },

    isEmptyBasket() {
      return this.basketItems.length === 0
    },

    articles() {
      return this.basketItems.filter(i => !i.collective || i.storageLocation !== 'warehouse').sort((a, b) => {
        if (a.manuallyAdded === b.manuallyAdded) return 0
        return a.manuallyAdded ? -1 : 1
      })
    },

    collectiveArticles() {
      return this.basketItems.filter(i => i.collective && i.storageLocation === 'warehouse').sort((a, b) => {
        if (a.manuallyAdded === b.manuallyAdded) return 0
        return a.manuallyAdded ? -1 : 1
      })
    }
  },

  methods: {
    reloadOrder() {
      this.generateOrder()
    },

    regenerateBasket() {
      this.generateOrder(true)
    },

    generateOrder(regenerate = false) {
      this.$store.dispatch('smartLogisticWarehouse/generateOrder', regenerate)
    },

    emptyOrder() {
      this.$store.dispatch('smartLogisticWarehouse/emptyOrder')
    },

    submitOrder() {
      this.$store.dispatch('smartLogisticWarehouse/submitOrder')
    }
  },

  mounted() {
    this.generateOrder()
  }
}
</script>

<style lang="scss" scoped>
.generate-tooltip-icon {
  margin-left: 6px;
  position: relative;
  top: -1px;
}

.generate-tooltip {
  width: 270px;
  color: #000000;
}

.generated-text {
  margin-left: 55px;
  line-height: 1;
}

.primary-light-button {
  background-color: #F0BCE5;


  &:hover {
    opacity: .9;
    &::before {
      display: none;
    }
  }
}

</style>
